import React, { useState } from 'react';
import PropTypes from 'prop-types';

import {
  ResponsiveContainer,
  AreaChart,
  XAxis,
  YAxis,
  Area,
  Tooltip,
} from 'recharts';

import { ReactorType } from '../../../utils/types';

const TICKS = [0, 12, 24];

function tickFormatter(value) {
  return value ? `${value}:00` : '00:00';
}

function ReactorLoadChart(props) {
  const { reactor, currentDate } = props;

  const [indexSelected, setSelected] = useState(null);

  // Add debugging
  console.log('ReactorLoadChart render:', {
    reactorName: reactor?.name,
    currentDate,
    productionDataLength: reactor?.dayProductions?.length,
    firstProduction: reactor?.dayProductions?.[0],
    lastProduction: reactor?.dayProductions?.[23],
    propsKeys: Object.keys(props),
    allProps: props,
  });

  // Add safety checks for data
  if (!reactor) {
    return <div>No reactor data</div>;
  }

  if (!reactor.dayProductions || !Array.isArray(reactor.dayProductions)) {
    return <div>No production data available</div>;
  }

  if (reactor.dayProductions.length === 0) {
    return <div>No production data for this date</div>;
  }

  // Transform data to include hour indices and handle null values
  // Filter out the extra 25th data point if it exists (should be 24 hours: 0-23)
  const rawData = reactor.dayProductions.slice(0, 24); // Ensure we only have 24 hours
  const chartData = rawData.map((dataPoint, index) => ({
    hour: index,
    value: dataPoint.value,
    hasData: dataPoint.value !== null, // Track whether this is real data
  }));

  console.log('ReactorLoadChart chartData:', {
    reactorName: reactor.name,
    originalDataLength: reactor.dayProductions.length,
    chartDataLength: chartData.length,
    sampleData: chartData.slice(0, 5),
    lastData: chartData[chartData.length - 1],
    indexSelected,
    validDataCount: chartData.filter(d => d.hasData).length,
  });

  const hour =
    indexSelected > 9 ? `${indexSelected}:00` : `0${indexSelected}:00`;
  const prod =
    indexSelected !== null &&
    reactor.dayProductions[indexSelected] &&
    reactor.dayProductions[indexSelected].value != null
      ? `${Math.floor(reactor.dayProductions[indexSelected].value)}`
      : '-';

  return (
    <div key={reactor.name} className="ReactorLoadChart">
      <div>{`${indexSelected !== null ? `À ${hour}: ${prod} ` : ''}MW`}</div>
      <div className="ReactorLoadChart__chart">
        <ResponsiveContainer width="100%" height={250}>
          <AreaChart
            data={chartData}
            onMouseMove={data => {
              // Handle hover for visual feedback
              if (data && data.activeTooltipIndex !== undefined) {
                // Visual feedback handled by activeDot
              }
            }}
            onClick={(data, index, event) => {
              // Handle click for data selection
              console.log('Chart clicked:', { data, index, event });

              // Try multiple ways to get the clicked hour
              if (data && typeof data.activeTooltipIndex === 'number') {
                console.log(
                  'Setting selected from activeTooltipIndex:',
                  data.activeTooltipIndex,
                );
                setSelected(data.activeTooltipIndex);
              } else if (data && data.activeLabel !== undefined) {
                const hourIndex = parseInt(data.activeLabel);
                console.log('Setting selected from activeLabel:', hourIndex);
                setSelected(hourIndex);
              } else if (data && data.activePayload && data.activePayload[0]) {
                const hourIndex = data.activePayload[0].payload.hour;
                console.log('Setting selected from activePayload:', hourIndex);
                setSelected(hourIndex);
              }
            }}
            margin={{ top: 10, right: 20, left: 10, bottom: 10 }}
          >
            <XAxis
              dataKey="hour"
              type="number"
              scale="linear"
              domain={[0, 23]}
              ticks={TICKS}
              tickFormatter={tickFormatter}
            />
            <YAxis
              domain={['dataMin - 50', 'dataMax + 50']}
              tickFormatter={value => Math.round(value)}
            />
            <Area
              dataKey="value"
              stroke="rgb(28, 114, 64)"
              fill="rgb(38, 166, 91)"
              fillOpacity={0.8}
              strokeWidth={3}
              dot={{
                r: 4,
                stroke: 'rgb(28, 114, 64)',
                fill: 'rgb(38, 166, 91)',
                strokeWidth: 2,
                style: { cursor: 'pointer' },
                onClick: data => {
                  console.log('Regular dot clicked:', data);
                  if (data && typeof data.payload?.hour === 'number') {
                    setSelected(data.payload.hour);
                  }
                },
              }}
              activeDot={{
                r: 8,
                stroke: 'rgb(28, 114, 64)',
                fill: 'rgb(20, 80, 45)',
                strokeWidth: 3,
                onClick: data => {
                  console.log('Active dot clicked:', data);
                  if (data && typeof data.payload?.hour === 'number') {
                    setSelected(data.payload.hour);
                  }
                },
                style: { cursor: 'pointer' },
              }}
              connectNulls={false}
            />
            <Tooltip
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  if (data && data.hasData) {
                    return (
                      <div
                        style={{
                          backgroundColor: 'white',
                          padding: '10px 14px',
                          border: '2px solid rgb(28, 114, 64)',
                          borderRadius: '6px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          fontSize: '14px',
                          fontWeight: '500',
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 'bold',
                            marginBottom: '6px',
                            color: '#333',
                          }}
                        >
                          {`${label}:00`}
                        </div>
                        <div
                          style={{
                            color: 'rgb(28, 114, 64)',
                            fontSize: '16px',
                            fontWeight: 'bold',
                          }}
                        >
                          {`${Math.floor(payload[0].value)} MW`}
                        </div>
                      </div>
                    );
                  }
                }
                return null;
              }}
              cursor={{
                stroke: 'rgb(28, 114, 64)',
                strokeWidth: 2,
                strokeDasharray: '5 5',
              }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

ReactorLoadChart.propTypes = {
  reactor: ReactorType.isRequired,
  currentDate: PropTypes.string.isRequired,
};

export default ReactorLoadChart;
